import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { Event } from 'app/core/models/event';
import { ErrorService } from 'app/core/services/error.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { ReplaySubject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { columnType } from '../table-utils/enums/columnType';


@Component({
  selector: 'app-errors-page',
  templateUrl: './errors-page.component.html'
})
export class ErrorsPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;

  errors : Map<number, Event> = new Map<number, Event>();
  statusItems: Array<String> = Array<String>();
  statusMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();

  columns: {
    data : {
      title: string;
      type: columnType
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0,
    sort: "createdDate,DESC",
    size: 20
  };

  criteria : Event = new Event();

  columnPropertyMapping = {
    "CREATION_DATE" : "createdDate",
    "ERROR_TYPE" : "eventType",
    "ERROR_SOURCE" : "eventSource",
    "POS_ID" : "posId",
    "ERROR" : "errorI18nKey",
    "CORRELATION_ID" : "correlationId"
  };

  searchForm : FormGroup = new FormGroup({
    from: new FormControl(),
    to: new FormControl(),
    merchantId: new FormControl(),
    supplierId: new FormControl(),
    merchantRef: new FormControl(),
    supplierRef: new FormControl(),
    eventType: new FormControl(),
    errorCode: new FormControl(),
    posId: new FormControl(),
    correlationId: new FormControl()
  })

  modalResponseSubscription: Subscription;
  eventType: Array<String> = Array<String>();
  eventTypeMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();
  allErrorsMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();
  errorTypes: Array<string> = Array<string>();

  constructor(
    private errorService: ErrorService,
    private translate: TranslateService,
    private notifService: NotifMessageService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    protected log: NGXLogger,
    public router: Router,
    private keycloakService: KeycloakService,
    private roleGuard: AuthenticatedRouteGuard
  ) { super(supplierService, merchantService, posService, log, router); }

  ngOnInit() {
    this.columns = {
      data : [
      { title: "CREATION_DATE", type: columnType.date },
      { title: "ERROR_TYPE", type: columnType.translateText },
      { title: "ERROR_SOURCE", type: columnType.text },
      { title: "POS_ID", type: columnType.text },
      { title: "ERROR", type: columnType.translateText },
      { title: "CORRELATION_ID", type: columnType.text }
    ],
    clickable : true
    };
    this.criteria.eventCategory = "ERROR_EVENT";
    this.getMerchants();
    this.getSuppliers();
    this.eventType = this.getTypeEvent();
    this.sortEventType();
    this.errorTypes = this.getAllErrors();
    this.sortError();

    if (this.errorTypes != null) {
      this.translate.onLangChange.subscribe(() => {
        this.sortError();
      });
    }

    this.translate.onLangChange.subscribe(() => {
      this.sortEventType();
    });

    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('EVENT_READ')) {
          this.loadData();
        }
      }
    });
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  formatError(error: Event) {
    this.errors.set(error.id, error);
    return {
      id: error.id,
      CREATION_DATE: error.createdDate,
      ERROR_TYPE : error.eventType,
      ERROR_SOURCE : error.eventSource,
      POS_ID: error.posId,
      ERROR: error.errorI18nKey,
      CORRELATION_ID: error.correlationId
    };
  }

  loadData(isLoadingSearch : boolean = false) : void {
    this.isLoadingResults = true;
    this.errorService
    .findByCriteria(this.criteria, this.pager)
    .pipe(
      tap(errors => this.totalLength$.next(errors.totalElements)),
      map(errors => {
        if (!errors) {
          this.isLoadingResults = false;
          return [];
        }
        if (errors.content.length === 0) {
          this.isLoadingResults = false;
          if (isLoadingSearch) {
            this.notifService.notifyInfo('EMPTY_RESULT');
          }
          return [];
        }
        if (errors.content.length !== 0) {
          this.isLoadingResults = false;
          return errors.content.map(error =>this.formatError(error));
        }
      }),
    ).subscribe(errors =>  {
      this.dataSubject$.next(errors);
    })
  }


  download() {
    this.errorService.findByCriteria(this.criteria, this.pager).subscribe(
      items => {
        if (items.totalElements > 10000) {
          this.notifService.notifyErrorWithoutDetail('EXPORT_NOTE');
        }
        this.errorService.downloadFile(items.content, 'Export_results');
      }
    );
  }

  sortEventType() {
    const eventTypes = [];
    this.getTypeEvent().forEach(element =>
      this.translate.get(element).subscribe((trad: string) => {
        eventTypes.push(trad);
        this.eventTypeMap.push({ element: element, traduction: trad });
        eventTypes.sort((a: string, b: string) => {
          return this.translate.instant(a).localeCompare(this.translate.instant(b))
        });
      }));
    this.eventType = eventTypes;
  }

  sortError() {
    const errors = [];
    this.getAllErrors().forEach(element =>
      this.translate.get(element).subscribe((trad: string) => {
        errors.push(trad);
        this.allErrorsMap.push({ element: element, traduction: trad });
        errors.sort((a: string, b: string) => {
          return this.translate.instant(a).localeCompare(this.translate.instant(b))
        });
      }));
    this.errorTypes = errors;
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex,
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "createdDate,DESC",
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new Event();
    this.criteria.eventCategory = "ERROR_EVENT";
    for (let field in this.searchForm.value) {
      if (!this.searchForm.value[field]) {
        delete this.searchForm.value[field];
      } else {
        if (field === "errorCode") {
        let trad = this.allErrorsMap.find(Type => Type.traduction == this.searchForm.value[field]);
        this.criteria["errorI18nKey"] = (typeof trad !== "undefined") ? trad.element : this.searchForm.value[field];
      } else if (field === "eventType") {
        let trad = this.eventTypeMap.find(Type => Type.traduction == this.searchForm.value[field]);
        this.criteria["eventType"] = (typeof trad !== "undefined") ? trad.element : this.searchForm.value[field];
      } else if (field === "from" || field === "to") {
          const fieldValue = this.searchForm.value[field];
          const date = new Date(fieldValue);
          date.setSeconds(0);
          date.setMilliseconds(0);
        this.criteria[field] = date.toISOString();
      } else {
        this.criteria[field] = this.searchForm.value[field]}
      }
    }
    if (this.criteria && this.criteria["from"] && this.criteria["to"] && this.criteria["from"] > this.criteria["to"]) {
      this.notifService.notifyErrorWithoutDetail("ERROR_DATE");
      return;
    }
    this.loadData(true);
  }

  details(errorId : number) {
    const error = this.errors.get(errorId);
    this.notifService.emitModalWithDetails("ERROR", error);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new Event();
    this.criteria.eventCategory = "ERROR_EVENT";
    this.loadData();
  }
}
